import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';

const countryCodes = [
  { code: '+1', country: 'US/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
  { code: '+48', country: 'Poland', flag: '🇵🇱' },
];

const HeroBanner = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    description: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation - only digits, max 10
  const validatePhone = (phone) => {
    const phoneRegex = /^\d{0,10}$/;
    return phoneRegex.test(phone);
  };

  const handleEmailChange = (value) => {
    setFormData({ ...formData, email: value });
    if (value && !validateEmail(value)) {
      setErrors({ ...errors, email: 'Please enter a valid email address (e.g., user@example.com)' });
    } else {
      setErrors({ ...errors, email: '' });
    }
  };

  const handlePhoneChange = (value) => {
    // Remove non-digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    if (digitsOnly.length <= 10) {
      setFormData({ ...formData, phone: digitsOnly });
      
      if (digitsOnly && digitsOnly.length !== 10) {
        setErrors({ ...errors, phone: 'Phone number must be exactly 10 digits' });
      } else {
        setErrors({ ...errors, phone: '' });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Final validation
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      hasErrors = true;
    }

    if (formData.phone.length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    toast({
      title: "Registration Request Received!",
      description: "Our trademark experts will contact you within 24 hours.",
    });
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
    setErrors({ email: '', phone: '' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-br from-gray-900 via-[#1a1a2e] to-gray-900 pt-32 pb-20 overflow-hidden">
      {/* Curved Background Shape */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#FF9900] rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500 rounded-full opacity-10 blur-3xl"></div>
      </div>

      {/* Diagonal Split Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF9900]/5 to-transparent" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)' }}></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            {/* Punching Upper Line */}
            <div className="inline-block bg-gradient-to-r from-[#FF9900] to-orange-600 text-black px-4 py-2 rounded-full text-sm font-semibold">
              US Trademark Registration Services | Online Trademark Filing | Register Brand on Amazon
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-white">
              Professional Trademark Registration Services in the US
            </h1>

            {/* Paragraph */}
            <p className="text-xl text-gray-300 leading-relaxed">
              Professional trademark registration services with online trademark filing, federal trademark protection, and complete USPTO trademark application support. Our trademark attorneys provide expert trademark lawyer guidance, comprehensive trademark search services, and Amazon Brand Registry enrollment assistance for businesses nationwide.
            </p>

            {/* Additional Benefits */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Complete Trademark Protection Package:</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-[#FF9900] mr-2">✓</span>
                  <span>USPTO trademark filing with federal trademark registration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#FF9900] mr-2">✓</span>
                  <span>Trademark search services and brand name availability check</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#FF9900] mr-2">✓</span>
                  <span>Amazon seller brand registry and brand verification support</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#FF9900] mr-2">✓</span>
                  <span>Trademark attorney consultation and legal trademark services</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Form CTA */}
          <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-8 border-2 border-[#FF9900]/30 backdrop-blur-sm">
            {/* Glowing Effect */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#FF9900]/20 to-orange-600/20 blur-xl -z-10 animate-pulse"></div>
            
            <div className="text-center mb-6">
              <div className="inline-block bg-gradient-to-r from-[#FF9900] to-orange-600 text-black px-4 py-2 rounded-full text-sm font-bold mb-4 shadow-lg shadow-orange-500/50">
                Special Offer: $45 Only!
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">
                Start Your Trademark Registration
              </h3>
              <p className="text-gray-400">
                Get started today and protect your brand with our expert services.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="hero-name" className="text-gray-300 font-semibold">Name *</Label>
                <Input
                  id="hero-name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-12 bg-gray-900/50 border-2 border-[#FF9900]/30 text-white placeholder:text-gray-500 focus:border-[#FF9900] focus:ring-2 focus:ring-[#FF9900]/50 transition-all"
                />
              </div>

              {/* Email */}
              <div className="space-y-1">
                <Label htmlFor="hero-email" className="text-gray-300 font-semibold">Email *</Label>
                <Input
                  id="hero-email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  required
                  className={`h-12 bg-gray-900/50 border-2 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-[#FF9900]/50 transition-all ${
                    errors.email ? 'border-red-500' : 'border-[#FF9900]/30 focus:border-[#FF9900]'
                  }`}
                />
                {errors.email && (
                  <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Contact Number with Country Code */}
              <div className="space-y-1">
                <Label htmlFor="hero-phone" className="text-gray-300 font-semibold">Contact number *</Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.countryCode}
                    onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                  >
                    <SelectTrigger className="w-[130px] h-12 bg-gray-900/50 border-2 border-[#FF9900]/30 text-white focus:border-[#FF9900] focus:ring-2 focus:ring-[#FF9900]/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-[#FF9900]/30 max-h-[300px]">
                      {countryCodes.map((item) => (
                        <SelectItem key={item.code} value={item.code} className="text-white hover:bg-gray-800">
                          <span className="flex items-center gap-2">
                            <span>{item.flag}</span>
                            <span>{item.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex-1">
                    <Input
                      id="hero-phone"
                      type="tel"
                      placeholder="1234567890"
                      value={formData.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      required
                      maxLength={10}
                      className={`h-12 bg-gray-900/50 border-2 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-[#FF9900]/50 transition-all ${
                        errors.phone ? 'border-red-500' : 'border-[#FF9900]/30 focus:border-[#FF9900]'
                      }`}
                    />
                  </div>
                </div>
                {errors.phone && (
                  <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
                )}
                <p className="text-gray-400 text-xs mt-1">Format: {formData.countryCode} + 10 digits (e.g., 1234567890)</p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="hero-description" className="text-gray-300 font-semibold">Description</Label>
                <Textarea
                  id="hero-description"
                  placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-gray-900/50 border-2 border-[#FF9900]/30 text-white placeholder:text-gray-500 focus:border-[#FF9900] focus:ring-2 focus:ring-[#FF9900]/50 transition-all resize-none"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#FF9900] to-orange-600 hover:from-[#FF9900]/90 hover:to-orange-600/90 text-black h-12 text-lg font-bold shadow-lg shadow-orange-500/50 hover:shadow-orange-500/70 transition-all"
              >
                Register My Trademark Now →
              </Button>

              <p className="text-xs text-gray-400 text-center">
                By submitting, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-24" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-gray-900"></path>
        </svg>
      </div>
    </section>
  );
};

export default HeroBanner;